import React, {useEffect, useState} from 'react';
import {connectLocation} from '../location/LocationConnectors';
import styled from '@emotion/styled';
import wrappedInReduxProvider from '../wrappedInReduxProvider';
import moment from 'moment';
import FilterListResults from '../ComponentLibrary/filter-list/oc-filter-list-results';
import CheckboxSelector from '../ComponentLibrary/filter-list/oc-filters';
import ContentHeader from './ContentHeader';
import SDSResultBlock from '../ComponentLibrary/filter-result-blocks/oc-sds-result-block';
import Container from '../OC/oc-container';
import {SortDropDown} from './SortDropDown';
import {BrowserRouter as Router, useHistory, useLocation} from "react-router-dom";
import {Breadcrumb, Breadcrumbs} from "../ComponentLibrary/oc-breadcrumbs";
import UtilityBar from "../ComponentLibrary/layout/oc-utility-bar";
import getFileSize from "../helpers/get_file_size";
import LocationSwitcher from "../location/Switcher";
import queryString from 'qs';
import _ from "lodash";

const MaxWidth = styled.div`
  width: 100%;
  padding-left: 50px;
`
const SuggestionsList = styled.ul`
  list-style: none;
  padding-left: 0;
`
const NoResultText = styled.p`
  margin-top: 20px;
`

const HorizontalLine = styled.hr`
  margin: 32px 0;
`

const NoResult = connectLocation(({ t, searchValue })  => {
  return (
    <MaxWidth>
      <NoResultText>{t(`sds.no_results_search_text`, {search_text: searchValue})}</NoResultText>
      <HorizontalLine/>
      <SuggestionsList>
        <li>
          <label><h3>{t(`sds.search_suggestions`)}</h3></label>
        </li>
        <SuggestionsList>
          <li>{t(`sds.check_spelling`)}</li>
          <li>{t(`sds.general_words`)}</li>
          <li>{t(`sds.different_words`)}</li>
        </SuggestionsList>
      </SuggestionsList>
    </MaxWidth>
  )
});

const ResultComponent = ({...props}) => <SDSResultBlock result={props}/>;

//used for sorting of filters
const compare = (a, b) => {
  return Object.values(a)[1] > Object.values(b)[1]
    ? 1
    : Object.values(b)[1] > Object.values(a)[1]
      ? -1
      : 0;
};

const snakeCase = (val) => val.toLowerCase().replace(/[^a-z0-9]+/g,'_').replace(/(^_|_$)/g,"");

const Disclaimer = connectLocation(({ t }) => {
  return (
    <div className="footnote">
      <p>{t(`sds.intro_p1`)}</p>
      <p>{t(`sds.intro_p2`)}</p>
    </div>
  );
});

const searchFields = ['sds_number', 'name'];

const SdsResults = connectLocation(({ t, locale, data, ...props }) => {
  const location = useLocation();
  const history = useHistory();

  const updatePathFromParams = (params) => {
    history.replace({ pathname: location.pathname, search: queryString.stringify(params) });
  }

  //options for sort select
  const sorts = {
    fields: {
      name_asc: {
        attribute: 'name',
        displayName: t('sds.sort_by.name_asc'),
        direction: 'asc',
      },
      name_desc: {
        attribute: 'name',
        displayName: t('sds.sort_by.name_desc'),
        direction: 'desc',
      },
      date_asc: {
        attribute: 'sds_update_date',
        displayName: t('sds.sort_by.publication_date_asc'),
        direction: 'asc',
      },
      date_desc: {
        attribute: 'sds_update_date',
        displayName: t('sds.sort_by.publication_date_desc'),
        direction: 'desc',
      },
      sds_number: {
        attribute: 'sds_number',
        displayName: t('sds.sort_by.sds_number'),
        direction: 'asc',
      },
    },
    component: SortDropDown,
    props: {label: t('sds.sort_by.sort_by')},
  };

  const categories = data.categories
    .map(val => ({
      value: val,
      displayName: t(`sds.categories.${val}`),
    }))
    .sort(compare);

  const languages = data.language_iso_codes
    .map(val => ({
      value: val,
      displayName: t(`languages.${val}`),
    }))
    .sort(compare);

  const regions = data.regions
    .map(val => ({
      value: val,
      displayName: t(`sds.regions.${val}`),
    }))
    .sort(compare);

  const suppliers = data.manufacturers
    .map(val => {
      let supplier = t(`sds.manufacturers.values.${snakeCase(val)}`);
      return {
        value: supplier,
        displayName: t(`sds.manufacturers.labels.${supplier}`),
      }
    })
    .sort(compare);


  const parseData = (items) => {
    return items.map(item => {
      let supplierValue = item.manufacturer ? t(`sds.manufacturers.values.${snakeCase(item.manufacturer)}`) : ''
      return {
        name: item.short_description,
        postheading: item.keywords,
        docUrl: item.binary_url,
        docName: `SDS# ${item.publication_id}`,
        docSize: getFileSize(item.size_bytes),
        docFormat: item.title.split(".")[1],
        docType: t(`sds.categories.${item.category}`),
        languageName: t(`languages.${item.language_iso_code}`),
        language: item.language_iso_code,
        supplierName: item.manufacturer ? t(`sds.manufacturers.labels.${supplierValue}`) : '',
        supplier: supplierValue, //required for filtering
        sup: item.manufacturer,
        regionName: t(`sds.regions.${item.region}`),
        region: item.region, //required for filtering
        date: moment(item.sds_update_date).format("MM-DD-YYYY"),
        sds_number: item.publication_id,
        sds_update_date: item.sds_update_date,
        document_type: item.category,
      }
    });
  };
  const [lists, setLists] = useState([{ name: 'sds_results', data: [] }]);
  const [loading, setLoading] = useState(true);

  const handleSearch = (searchValue, urlParams)  => {
    if(searchValue === urlParams.q) return //do not hit API if the keyword is same
    setLoading(true);
    let params = Object.assign(urlParams, {q: searchValue});
    updatePathFromParams(params)
    getData(searchValue);
  }

  const getData = async (searchValue) => {
    searchValue ||= ""; //replace undefined with empty string
    let body;
    try {
      const res = await fetch(`/${locale.code.toLowerCase()}/sds/results?q=${searchValue}`, {headers: {'Accept': 'application/json' }})
      body = await res.json()
    }catch (e){
      setLoading(false);
      return;
    }
    setLists([{ name: 'sds_results', data: parseData(body.items) }]);
    setLoading(false);
  }

  useEffect(() => {
    getData(queryString.parse(location.search, {ignoreQueryPrefix: true}).q)
  }, []);

  const filters = {
    document_type: {
      component: CheckboxSelector,
      displayName: t(`sds.fields.document_type`),
      options: categories,
      valueType: 'multiple',
    },
    language: {
      component: CheckboxSelector,
      displayName: t(`sds.fields.language`),
      options: languages,
      displayLimit: 5,
      valueType: 'multiple',
    },
    region: {
      component: CheckboxSelector,
      displayName: t(`sds.fields.region`),
      options: regions,
      valueType: 'multiple',
      filterFunc: (item, valueToMatch) => {
        if(item.region === 'global') return true;
        return (valueToMatch || []).every(filter => item.region.includes(filter))
      },
    },
    supplier: {
      component: CheckboxSelector,
      displayName: t(`sds.fields.supplier`),
      options: _.uniqBy(suppliers, 'value'),
      valueType: 'multiple',
    },
    noOperation: {
      component: Disclaimer,
      options: [],
     }
  };

  return (
    <>
      <UtilityBar>
        <Breadcrumbs>
          <Breadcrumb href="/" label="Owens Corning" />
          <Breadcrumb href="/sds" label="SDS" />
        </Breadcrumbs>
        <LocationSwitcher forceReload={true} showLocale={true} />
      </UtilityBar>
      <Container maxWidth="1170">
        <FilterListResults
          contentHeader={{
            component: ContentHeader,
            props: { handleSearch, loading }
          }}
          filters={filters}
          lists={lists}
          resultComponent={ResultComponent}
          noResultComponent={NoResult}
          allowSearch={true}
          searchFields={searchFields}
          sorts={sorts}
          loading={loading}
        />
      </Container>
    </>
  );
});

export default wrappedInReduxProvider((props) => (
  <Router>
    <SdsResults {...props}/>
  </Router>
));
